/**
 * -----------------------------------------------------------------------------
 * User Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the form for editing the user details.
 */

import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GroupBase, OptionsOrGroups } from "react-select";
import {
  useLazyOrganizationGroupsQuery,
  useOrganizationRolesQuery,
  useOrganizationSystemRolesQuery,
} from "../../../api/network/adminApiServices";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import {
  LegacyGroupEntityListingPayload,
  OrgGHGroup,
  TLegacyGroup,
} from "../../../api/types/Group";
import { LegacyRoleEntityListingPayload } from "../../../api/types/UserRole";
import svgIcons from "../../../assets/svg/svgIcons";
import FormActionButtons from "../../../components/admin/formActionButtons";
import { valuePreviewFilterSessionTimeout } from "../../../components/admin/formFieldDurationInput";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  AdminFormFieldDurationInput,
  AdminFormFieldInput,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  PillItem,
  PseudoSelect,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HierarchyGroupSelector from "../../../components/common/hierarchyGroupSelector";
import { APP_CONFIG, TIMEZONE_OPTIONS } from "../../../constants/constants";
import { asyncDebounce } from "../../../utils/common";
import { useLoggedInUserData } from "../../../utils/user";
import {
  DEFAULT_OVERLAY_OPTIONS,
  FIRST_LOGIN_PAGE_OPTIONS,
  MAP_TYPE_OPTIONS,
  USER_STATE_OPTIONS,
} from "./constants";

interface UserGeneralDetailFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean;
  showUserAcl?: boolean;
  setShowUserAcl?: (value: boolean) => void;
}

export const UserGeneralDetailForm: FC<UserGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  showUserAcl,
  setShowUserAcl,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.users.detailsPage.form",
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        label="User ID"
        type="text"
        id="user_id"
        name="user_id"
        value={values.user_id}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.user_id}
        error={errors.user_id}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label="Password"
        type="password"
        id="password"
        name="password"
        value={values.password}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.password}
        error={errors.password}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      <div className="col-span-12">
        <AdminFormFieldInput
          label="User Description"
          type="text"
          id="user_description"
          name="user_description"
          value={values.user_description}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.user_description}
          error={errors.user_description}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>
      <div className="col-span-12 grid grid-cols-12 gap-4">
        <AdminFormFieldCheckbox
          label="Active"
          id="is_active"
          type="checkbox"
          name="is_active"
          checked={values.is_active}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.is_active}
          error={errors.is_active}
          disabled={!userCanEdit}
        />

        {/* <AdminFormFieldCheckbox
          label="Use Two Factor"
          id="use_two_factor"
          type="checkbox"
          name="use_two_factor"
          checked={values.use_two_factor}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.use_two_factor}
          error={errors.use_two_factor}
          disabled={!userCanEdit}
        /> */}

        <AdminFormFieldCheckbox
          label="Use Geozone Labels"
          id="use_geozone_labels"
          type="checkbox"
          name="use_geozone_labels"
          checked={values.use_geozone_labels}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.use_geozone_labels}
          error={errors.use_geozone_labels}
          disabled={!userCanEdit}
        />
      </div>

      <AdminFormFieldInput
        label="Contact Name"
        type="text"
        id="contact_name"
        name="contact_name"
        value={values.contact_name}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.contact_name}
        error={errors.contact_name}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label="Contact Phone Number"
        type="text"
        id="contact_phone_number"
        name="contact_phone_number"
        value={values.contact_phone_number}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.contact_phone_number}
        error={errors.contact_phone_number}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label="Contact Email"
        type="email"
        id="contact_email"
        name="contact_email"
        value={values.contact_email}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.contact_email}
        error={errors.contact_email}
        disabled={!userCanEdit}
        // readOnly={true}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label="Timezone"
        id="timezone"
        name="timezone"
        value={values.timezone}
        options={TIMEZONE_OPTIONS}
        onChange={(e: any) => {
          formikSetValue("timezone", e?.value);
        }}
        onBlur={(e: any) => {
          formikSetTouched("timezone", true);
          handleBlur(e);
        }}
        touched={touched.timezone}
        error={errors.timezone}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      {/* <div className="col-span-12 grid grid-cols-8">
        <AdminFormFieldCheckbox
          customWrapperClass="col-span-4 lg:mr-2"
          label="Enable SSO to Visatracks"
          id="enable_sso_to_visatracks"
          type="checkbox"
          name="enable_sso_to_visatracks"
          checked={values.enable_sso_to_visatracks}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.enable_sso_to_visatracks}
          error={errors.enable_sso_to_visatracks}
          disabled={!userCanEdit}
        />
      </div> */}

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label="Default Overlay"
        id="default_overlay"
        name="default_overlay"
        value={values.default_overlay}
        options={DEFAULT_OVERLAY_OPTIONS}
        onChange={(e) => {
          formikSetValue("default_overlay", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("default_overlay", true);
          handleBlur(e);
        }}
        touched={touched.default_overlay}
        error={errors.default_overlay}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label="Default Map Type"
        id="mapDefault"
        name="mapDefault"
        value={values.mapDefault}
        options={MAP_TYPE_OPTIONS}
        onChange={(e) => {
          formikSetValue("mapDefault", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("mapDefault", true);
          handleBlur(e);
        }}
        touched={touched.mapDefault}
        error={errors.mapDefault}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label="User State"
        id="user_state"
        name="user_state"
        value={values.user_state || undefined}
        placeholder="Select user state..."
        options={USER_STATE_OPTIONS}
        onChange={(e) => {
          formikSetValue("user_state", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("user_state", true);
          handleBlur(e);
        }}
        touched={touched.user_state}
        error={errors.user_state}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldDurationInput
        label="Session Timeout"
        id="session_timeout"
        name="session_timeout"
        value={values.session_timeout}
        valuePreviewFilter={valuePreviewFilterSessionTimeout}
        loading={loadingData}
        onChange={(totalSeconds) => {
          formikSetValue("session_timeout", totalSeconds);
        }}
        onBlur={() => {
          formikSetTouched("session_timeout", true);
        }}
        touched={touched.session_timeout}
        error={errors.session_timeout}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label="First Login Page"
        id="first_login_page"
        name="first_login_page"
        placeholder="Select first login page..."
        value={values.first_login_page || undefined}
        options={FIRST_LOGIN_PAGE_OPTIONS}
        onChange={(e) => {
          formikSetValue("first_login_page", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("first_login_page", true);
          handleBlur(e);
        }}
        touched={touched.first_login_page}
        error={errors.first_login_page}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
    </div>
  );
};

export const UserAuthorizedGroupsForm: FC<UserGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.users.detailsPage",
  });
  const { t: tMain } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");

  // prepare group query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgGroupsQueryParams, setOrgGroupsQueryParams] =
    React.useState<LegacyGroupEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    });

  // // fetch organization groups
  // const { data: initialGroups, isFetching: isFetchingInitialGroups } =
  //   useOrganizationGroupsQuery(orgGroupsQueryParams);

  // fetch searched groups
  const [orgGroupsApiTrigger, { isFetching: isFetchingGroups }] =
    useLazyOrganizationGroupsQuery();

  const loadOptionsHandlerGroups = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const newOrgGroupsQueryParams = {
      ...orgGroupsQueryParams,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.GROUP_DROPDOWN.join(","), // search by groupid only
      page: (additional?.page as number) ?? 2,
    };
    setOrgGroupsQueryParams(newOrgGroupsQueryParams);
    const { data: groupsResponse } = await orgGroupsApiTrigger(
      newOrgGroupsQueryParams
    );
    if (APP_CONFIG.DEBUG.ALL) console.log("groupsResponse", groupsResponse);
    if (!groupsResponse || !groupsResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 2,
        },
      };
    }
    const { results } = groupsResponse as { results: TLegacyGroup[] };
    return {
      options: !!results
        ? (results || ([] as TLegacyGroup[]))
            ?.filter(
              (item) => !values.selected_groups.includes(`${item.groupid}`)
            )
            .map((item: TLegacyGroup, index: number) => ({
              value: `${item.groupid}`,
              label: item.displayname || item.groupid,
            }))
            .filter(
              (item: TSelectboxOption) =>
                !values.selected_groups.includes(item.value)
            )
        : [],
      hasMore: groupsResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };

  const debouncedLoadOptionsHandlerGroups = asyncDebounce(
    loadOptionsHandlerGroups,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  // const handleGroupChange = async (e: any) => {
  //   const newlySelectedGroup = e.value;
  //   if (!!e.value) {
  //     formikSetValue("selected_groups", [
  //       ...values.selected_groups,
  //       newlySelectedGroup,
  //     ]);
  //   }
  // };
  const handleGroupChange = async (e: any) => {
    const newlySelectedGroup = e;
    if (!!e) {
      formikSetValue("selected_groups", [...newlySelectedGroup]);
    }
  };

  // add all groups to vehicle
  const addAllGroups = () => {
    formikSetValue("selected_groups", []);
  };

  // handle group removal
  const onRemoveFromGroup = (option: any) => {
    formikSetValue(
      "selected_groups",
      values.selected_groups.filter(
        (selectedGroup: any) => selectedGroup.value !== option
      )
    );
  };

  const MemoizedFormActionSelectGroupsButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: tMain("select_groups_button"),
              type: "button",
              variant: "primary-like",
              customWrapperClass: "col-span-3",
              onClick: () => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showHierarchyGroupSelector: true,
                  })
                );
              },
              disabled: !userCanEdit || loadingData || !!values.all_groups, // disable if user cannot edit or all groups is selected
            },
          ]}
        />
      </>
    );
  }, [userCanEdit, loadingData, values.all_groups, modalsState]);

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <div className="col-span-12 grid grid-cols-8">
        <AdminFormFieldCheckbox
          label="All Groups"
          id="all_groups"
          type="checkbox"
          name="all_groups"
          checked={!!values.all_groups}
          onChange={(e) => {
            handleChange?.(e);
            addAllGroups();
            setTimeout(() => {
              formikSetTouched("selected_groups");
            }, 100);
          }}
          onBlur={handleBlur}
          error={errors.all_groups}
          touched={touched.all_groups}
          disabled={!userCanEdit}
          customWrapperClass="col-span-4 lg:mr-2"
        />
      </div>
      {errors.selected_groups && userCanEdit && (
        <div className="col-span-12 bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs">
          {errors.selected_groups}
        </div>
      )}
      {loadingData ? (
        <PseudoSelect label={t("select_group_to_add")} />
      ) : (
        <>
          {accountHasGroupHierarchyEnabled === "1" ? (
            <>
              {/* <AdminFormFieldSubmit
                  label={tMain('select_groups_button')}
                  type="button"
                  variant="primary-like"
                  customWrapperClass="col-span-3"
                  onClick={() => { dispatch(setModalsData({ ...modalsState, showHierarchyGroupSelector: true, })) }}
                  disabled={!userCanEdit || loadingData || !!values.all_groups} // disable if user cannot edit or all groups is selected
                /> */}
              {MemoizedFormActionSelectGroupsButton}
              {modalsState.showHierarchyGroupSelector && (
                <HierarchyGroupSelector
                  initiallySelectedGroups={values.selected_groups.map(
                    (item: any) => item.value
                  )}
                  onSelectionChange={(selectedGroups: string[]) => {
                    if (APP_CONFIG.DEBUG.DISPLAY_VERBOSE)
                      console.log("VALUES RECEIVED:", selectedGroups);
                    formikSetValue(
                      "selected_groups",
                      selectedGroups?.map((item) => ({
                        label: `${item}`,
                        value: `${item}`,
                      })) || []
                    );
                    setTimeout(() => {
                      formikSetTouched("selected_groups");
                    }, 100); // to trigger validation
                  }}
                />
              )}
            </>
          ) : (
            <AdminFormFieldAsyncLazyDropdown
              customWrapperClass="col-span-12"
              key={`group_selector_`} // causes re-render on group selection
              loadingData={loadingData || isFetchingGroups}
              label={t("select_group_to_add")}
              id="group_selector"
              name="group_selector"
              noOptionsMessage={() => t("group_selector_no_options")}
              // defaultOption={{ value: `${values.group_selector}`, label: `${groupDisplayName(`${values.streetaddress}`, "-")}` }}
              // defaultOptions={initialGroups?.results
              //   ?.filter(
              //     (item) => !values.selected_groups.includes(`${item.groupid}`)
              //   )
              //   ?.map((item: TLegacyGroup) => ({
              //     value: `${item.groupid}`,
              //     label: item.displayname || item.groupid,
              //   }))}
              isMulti={true}
              placeholder="Type to search groups..."
              additional={{ page: 1 }}
              loadOptions={debouncedLoadOptionsHandlerGroups}
              onChange={handleGroupChange}
              onBlur={(e) => {
                formikSetTouched("selected_groups", true);
                handleBlur(e);
              }}
              error={errors.group_selector}
              value={values.selected_groups}
              touched={touched.group_selector}
              disabled={!userCanEdit || loadingData || values.all_groups}
              detailsFormField={true}
              menuPlacement="top"
            />
          )}
        </>
      )}
      {accountHasGroupHierarchyEnabled === "1" && (
        <div className="col-span-12 grid grid-cols-12">
          <AdminFormFieldPillList
            disabled={!userCanEdit || values.all_groups}
            isError={errors.selected_groups && userCanEdit}
          >
            {values.selected_groups.map(
              (group: string | { label: string; value: string }, i: number) => (
                <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                  key={`pillItem_${i}`}
                  disabled={!userCanEdit || values.all_groups}
                  item={
                    {
                      name: typeof group === "string" ? group : group.label,
                    } as PillItem
                  }
                  onRemove={() =>
                    onRemoveFromGroup(
                      typeof group === "string" ? group : group.value
                    )
                  }
                  onItemClick={
                    accountHasGroupHierarchyEnabled === "1"
                      ? (item) => {
                          dispatch(
                            setModalsData({
                              ...modalsState,
                              showReadOnlyViewForHierarchyGroup: {
                                accountid: thisUserOrganizationId,
                                active_inactive_vehicle_counts: {
                                  active_vehicle_count: 0,
                                  inactive_vehicle_count: 0,
                                },
                                description: "",
                                groupid:
                                  typeof group === "string"
                                    ? group
                                    : group.value,
                              } as OrgGHGroup,
                              showHierarchyGroupSelector: true,
                            })
                          );
                        }
                      : undefined
                  }
                />
              )
            )}
          </AdminFormFieldPillList>
        </div>
      )}
    </div>
  );
};

export const UserAccessControlForm: FC<UserGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  showUserAcl,
  setShowUserAcl,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.users.detailsPage",
  });

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const { data: dataRoles, isFetching: isFetchingRoles } =
    useOrganizationRolesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
      search: "",
    } as LegacyRoleEntityListingPayload);

  const { data: dataSystemRoles, isFetching: isFetchingSystemRoles } =
    useOrganizationSystemRolesQuery();

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldDropdown
        loadingData={loadingData || isFetchingRoles || isFetchingSystemRoles}
        label={t("role")}
        id="default_acl_role"
        name="default_acl_role"
        value={values.default_acl_role}
        options={
          // userRoleIsPrivileged(values.default_acl_role)
          //   ? [{
          //     value: values.default_acl_role,
          //     label: values.default_acl_role,
          //   }]
          //   :
          [
            ...(dataRoles?.results?.map((role) => ({
              value: role?.roleid,
              label: role?.roleid,
            })) || []),
            ...(dataSystemRoles?.map((role) => ({
              value: role?.roleid,
              label: role?.roleid,
            })) || []),
          ]
        }
        onChange={(e) => {
          formikSetValue("default_acl_role", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("default_acl_role", true);
          handleBlur(e);
        }}
        touched={touched.default_acl_role}
        error={errors.default_acl_role}
        disabled={!userCanEdit}
        // readOnly={userRoleIsPrivileged(values.default_acl_role)}
        detailsFormField={true}
      />

      {setShowUserAcl && (
        <div className="col-start-11 col-span-2 pt-8">
          <button
            type="button"
            onClick={() => setShowUserAcl(!showUserAcl)}
            className="flex items-center whitespace-nowrap font-semibold text-large text-accent-blue-dark hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Advanced
            <img
              src={showUserAcl ? svgIcons.ExpandMoreIcon : svgIcons.ExpandIcon}
              alt="advanced"
              className="ml-1 w-4 h-4"
            />
          </button>
        </div>
      )}
      {/* <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t('maximum_access_level')}
        id="maximum_access_level"
        name="maximum_access_level"
        value={`${values.maximum_access_level}`}
        options={ROLE_ACCESS_LEVELS_OPTIONS.ALL}
        onChange={(e) => { formikSetValue('maximum_access_level', e?.value); }}
        onBlur={(e) => { formikSetTouched("maximum_access_level", true); handleBlur(e); }}
        touched={touched.maximum_access_level}
        error={errors.maximum_access_level}
        disabled={!userCanEdit}
        readOnly={userRoleIsPrivileged(values.default_acl_role)}
        detailsFormField={true}
      /> */}
    </div>
  );
};
